import { CONSTRAINT_PANEL, TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceUserActions from "../../constants/interfaceUserActions";
import { appStateActionTypes, appStateSelectors } from "../../modules/appState";
import { timelineViewSettingsSelectors } from "../../modules/definition/timelineViewSettings";
import { dependencyActionTypes } from "../../modules/dependencies";
import { itemSelectors } from "../../modules/items";
import { uiStateActionTypes } from "../../modules/uiState";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getFlashPlaceholder,
  getIconPlaceholder,
  getSwitchViewInstruction,
  getTipIdGenerator,
  Placeholders
} from "../utils";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "DEPENDENCIES_AND_CONSTRAINTS";
const GROUP_LABEL = "Dependencies and Constraints";
const GROUP_DESCRIPTION = `Learn how to keep ${Placeholders.EVENT_PLURAL_LABEL} in order, and be warned if constraints are violated.`;
const GROUP_GATE = state => itemSelectors.getAllEventIdsDisplayedOnTimeline(state).size >= 5;
const GROUP_GATE_MESSAGE = `You must have at least 5 ${Placeholders.EVENT_PLURAL_LABEL} in your timeline to begin this tutorial.`;
const GROUP_APPLICABLE_GATE = null;
const GROUP_APPLICABLE_GATE_MESSAGE = null;
const GROUP_COMPLETED_MESSAGE = "Great work! Continue add dependencies and constraints to suit your timeline's logic.";
const GROUP_LINKS = [
  { label: "Dependencies and Constraints", path: "https://help.timeline.app/article/142-dependencies-and-constraints" }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("DEPENDENCIES_VS_CONSTRAINTS"),
      label: "How dependencies and constraints differ",
      steps: [
        {
          label: `Dependencies enforce ${Placeholders.AN_EVENT_LABEL} order by specifing which ${Placeholders.EVENT_PLURAL_LABEL} must come before others`,
          isNumbered: false
        },
        {
          label: `Constraints provide advanced options, such as specifying exact offsets between ${Placeholders.EVENT_PLURAL_LABEL}, or constraints related to fixed dates`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("CHOOSE_DEPENDENCY_SETTING"),
      label: "Choose automatic or manual dependency resolution",
      steps: [
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.SETTINGS,
            "Settings icon"
          )} ${getIconPlaceholder("sidebarSettings")} in the toolbar to open Timeline Settings`,
          isNumbered: true
        },
        {
          label:
            "Under the Calendar & Date > Automatic Calculations section, choose whether or not you want items to be automatically adjusted.",
          isNumbered: true
        },
        {
          label:
            "Tip: If a dependency becomes violated, depending on your settings your timeline will either show a warning or automatically move items to satisfy the dependency.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: action => action.type === uiStateActionTypes.HIDE_SETTINGS_MODAL
    },
    {
      id: idGenerator("ADD_DEPENDENCY"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} ends before another ${Placeholders.EVENT_LABEL} starts`,
      steps: [
        { label: `Click on ${Placeholders.AN_EVENT_LABEL} to select it.`, isNumbered: true },
        {
          label: `In the Inspector, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_CONSTRAINTS_TAB,
            "constraints icon"
          )} ${getIconPlaceholder("inspectorConstraints")} to show the Dependencies & Constraints tab`,
          isNumbered: true
        },
        {
          label: `Click the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_BLOCKED_BY_PLUS,
            "+ icon"
          )} alongside "Blocked by" and choose ${
            Placeholders.AN_EVENT_LABEL
          } that must be completed before the selected ${Placeholders.EVENT_LABEL} can start.`,
          isNumbered: true
        }
      ],
      requiresInspector: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action => action.type === dependencyActionTypes.CREATE_DEPENDENCIES
    },
    {
      id: idGenerator("ADD_RELATIVE_CONSTRAINT"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} starts exactly when another ${Placeholders.EVENT_LABEL} ends`,
      steps: [
        { label: `Click on ${Placeholders.AN_EVENT_LABEL} to select it.`, isNumbered: true },
        {
          label: `In the Inspector, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_CONSTRAINTS_TAB,
            "constraints icon"
          )} ${getIconPlaceholder("inspectorConstraints")} to show the Dependencies & Constraints tab`,
          isNumbered: true
        },
        {
          label: `Click the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_DATE_CONSTRAINTS_PLUS,
            "+ icon"
          )} alongside "Date Constraints"`,
          isNumbered: true
        },
        {
          label: `Set the options to "End equals start of" and choose ${Placeholders.AN_EVENT_LABEL} to start exactly when the selected event ends.`,
          isNumbered: true
        },
        { label: "Click Add to create the constraint.", isNumbered: true }
      ],
      requiresInspector: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_RELATIVE_CONSTRAINT
    },
    {
      id: idGenerator("ADD_DATE_CONSTRAINT"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} starts after a specified date`,
      steps: [
        { label: `Click on ${Placeholders.AN_EVENT_LABEL} to select it.`, isNumbered: true },
        {
          label: `In the Inspector, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_CONSTRAINTS_TAB,
            "constraints icon"
          )} ${getIconPlaceholder("inspectorConstraints")} to show the Dependencies & Constraints tab`,
          isNumbered: true
        },
        {
          label: `Click the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_DATE_CONSTRAINTS_PLUS,
            "+ icon"
          )} alongside "Date Constraints"`,
          isNumbered: true
        },
        { label: 'Set the options to "Start" and "is after date" and enter a date.', isNumbered: true },
        { label: "Tap Add to create the constraint.", isNumbered: true }
      ],
      requiresInspector: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_DATE_CONSTRAINT
    },
    {
      id: idGenerator("SHOW_DEPENDENCIES_TIMELINE"),
      label: "Show dependencies and constraints on your timeline",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `In the view footer, check that the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_SHOW_DEPENDENCIES,
            "dependency icon"
          )} ${getIconPlaceholder("footerTimelineDependencyLines")} is active (blue).`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      canSkip: true,
      dismissalGate: state =>
        appStateSelectors.getActiveViewType(state) === TIMELINE_VIEW &&
        timelineViewSettingsSelectors.getTimelineViewSettings(state).showDependencies
    },
    {
      id: idGenerator("SHOW_CONSTRAINT_PANEL"),
      label: "See what dependencies and constraints are active",
      steps: [
        {
          label: `In the sidebar, click the ${getFlashPlaceholder(
            interfaceIdentifiers.CONSTRAINTS,
            "constraints icon"
          )} ${getIconPlaceholder("inspectorConstraints")} to open the Dependencies & Constraints panel.`,
          isNumbered: true
        },
        { label: "All of your timeline's dependencies and constraints will be listed.", isNumbered: true },
        {
          label:
            "Tip: If you have any violated dependencies or constraints, the sidebar icon will show a warning badge.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      actionInstruction: `Open constraints panel to continue`,
      requiresDismissal: true,
      dismissalGate: state => {
        const leftPanelMode = appStateSelectors.getLeftPanelMode(state);
        return leftPanelMode === CONSTRAINT_PANEL;
      }
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("DEPENDENCIES_VS_CONSTRAINTS"),
      label: "How dependencies and constraints differ",
      steps: [
        {
          label: `Dependencies enforce ${Placeholders.AN_EVENT_LABEL} order by specifing which ${Placeholders.EVENT_PLURAL_LABEL} must come before others`,
          isNumbered: false
        },
        {
          label: `Constraints provide advanced options, such as specifying exact offsets between ${Placeholders.EVENT_PLURAL_LABEL}, or constraints related to fixed dates`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("CHOOSE_DEPENDENCY_SETTING"),
      label: "Choose automatic or manual dependency resolution",
      steps: [
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.SETTINGS,
            "Settings icon"
          )} ${getIconPlaceholder("sidebarSettings")} in the toolbar to open Timeline Settings`,
          isNumbered: true
        },
        {
          label:
            "Under the Calendar & Date > Automatic Calculations section, choose whether or not you want items to be automatically adjusted.",
          isNumbered: true
        },
        {
          label:
            "Tip: If a dependency becomes violated, depending on your settings your timeline will either show a warning or automatically move items to satisfy the dependency.",
          isNumbered: false
        }
      ],
      accessGate: null,
      requiresExitEditMode: true,
      gateMessage: null,
      resolve: action => action.type === uiStateActionTypes.HIDE_SETTINGS_MODAL
    },
    {
      id: idGenerator("ADD_DEPENDENCY"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} ends before another ${Placeholders.EVENT_LABEL} starts`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Constraints"`, isNumbered: true },
        {
          label: `Tap the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_BLOCKED_BY_PLUS,
            "+ icon"
          )} alongside "Blocked by" and choose ${
            Placeholders.AN_EVENT_LABEL
          } that must be completed before the selected ${Placeholders.EVENT_LABEL} can start.`,
          isNumbered: true
        }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action => action.type === dependencyActionTypes.CREATE_DEPENDENCIES
    },
    {
      id: idGenerator("ADD_RELATIVE_CONSTRAINT"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} starts exactly when another ${Placeholders.EVENT_LABEL} ends`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Constraints"`, isNumbered: true },
        {
          label: `Tap the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_DATE_CONSTRAINTS_PLUS,
            "+ icon"
          )} alongside "Date Constraints"`,
          isNumbered: true
        },
        {
          label: `Set the options to "End"  and "equals start of" and choose ${Placeholders.AN_EVENT_LABEL} to start exactly when the selected event ends.`,
          isNumbered: true
        },
        { label: "Tap Add to create the constraint.", isNumbered: true }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_RELATIVE_CONSTRAINT
    },
    {
      id: idGenerator("ADD_DATE_CONSTRAINT"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} starts after a specified date`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Constraints"`, isNumbered: true },
        {
          label: `Tap the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_DATE_CONSTRAINTS_PLUS,
            "+ icon"
          )} alongside "Date Constraints"`,
          isNumbered: true
        },
        { label: 'Set the options to "Start" and  "is after date" and enter a date.', isNumbered: true },
        { label: "Tap Add to create the constraint.", isNumbered: true }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_DATE_CONSTRAINT
    },
    {
      id: idGenerator("SHOW_DEPENDENCIES_TIMELINE"),
      label: "Show dependencies and constraints on your timeline",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        { label: `In the View Settings panel, check that "Show dependency lines" is turned on`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      canSkip: true,
      dismissalGate: state =>
        appStateSelectors.getActiveViewType(state) === TIMELINE_VIEW &&
        timelineViewSettingsSelectors.getTimelineViewSettings(state).showDependencies
    }
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("DEPENDENCIES_VS_CONSTRAINTS"),
      label: "How dependencies and constraints differ",
      steps: [
        {
          label: `Dependencies enforce ${Placeholders.AN_EVENT_LABEL} order by specifing which ${Placeholders.EVENT_PLURAL_LABEL} must come before others`,
          isNumbered: false
        },
        {
          label: `Constraints provide advanced options, such as specifying exact offsets between ${Placeholders.EVENT_PLURAL_LABEL}, or constraints related to fixed dates`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("CHOOSE_DEPENDENCY_SETTING"),
      label: "Choose automatic or manual dependency resolution",
      steps: [
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.SETTINGS,
            "Settings icon"
          )} ${getIconPlaceholder("sidebarSettings")} in the toolbar to open Timeline Settings`,
          isNumbered: true
        },
        {
          label:
            "Under the Calendar & Date > Automatic Calculations section, choose whether or not you want items to be automatically adjusted.",
          isNumbered: true
        },
        {
          label:
            "Tip: If a dependency becomes violated, depending on your settings your timeline will either show a warning or automatically move items to satisfy the dependency.",
          isNumbered: false
        }
      ],
      accessGate: null,
      requiresExitEditMode: true,
      gateMessage: null,
      resolve: action => action.type === uiStateActionTypes.HIDE_SETTINGS_MODAL
    },
    {
      id: idGenerator("ADD_DEPENDENCY"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} ends before another ${Placeholders.EVENT_LABEL} starts`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Constraints"`, isNumbered: true },
        {
          label: `Tap the + icon alongside "Blocked by" and choose ${Placeholders.AN_EVENT_LABEL} that must be completed before the selected ${Placeholders.EVENT_LABEL} can start.`,
          isNumbered: true
        }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action => action.type === dependencyActionTypes.CREATE_DEPENDENCIES
    },
    {
      id: idGenerator("ADD_RELATIVE_CONSTRAINT"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} starts exactly when another ${Placeholders.EVENT_LABEL} ends`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Constraints"`, isNumbered: true },
        { label: `Tap the + icon alongside "Date Constraints"`, isNumbered: true },
        {
          label: `Set the options to "End"  and "equals start of" and choose ${Placeholders.AN_EVENT_LABEL} to start exactly when the selected event ends.`,
          isNumbered: true
        },
        { label: "Tap Add to create the constraint.", isNumbered: true }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_RELATIVE_CONSTRAINT
    },
    {
      id: idGenerator("ADD_DATE_CONSTRAINT"),
      label: `Ensure ${Placeholders.AN_EVENT_LABEL} starts after a specified date`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Constraints"`, isNumbered: true },
        { label: `Tap the + icon alongside "Date Constraints"`, isNumbered: true },
        { label: 'Set the options to "Start" and  "is after date" and enter a date.', isNumbered: true },
        { label: "Tap Add to create the constraint.", isNumbered: true }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_DATE_CONSTRAINT
    },
    {
      id: idGenerator("SHOW_DEPENDENCIES_TIMELINE"),
      label: "Show dependencies and constraints on your timeline",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the more icon ${getIconPlaceholder(
            "ellipsis"
          )} in the toolbar and then View settings ${getIconPlaceholder("footerCardDetails")}`,
          isNumbered: true
        },
        { label: `In the View Settings panel, check that "Show dependency lines" is turned on`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      canSkip: true,
      dismissalGate: state =>
        appStateSelectors.getActiveViewType(state) === TIMELINE_VIEW &&
        timelineViewSettingsSelectors.getTimelineViewSettings(state).showDependencies
    }
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
